namespace distill.dashboards.charts {

    const chartInstances: { [id: string]: echarts.ECharts } = {};
    const renderFrames: { [id: string]: number } = {};
    const chartObservers: { [id: string]: ResizeObserver } = {};

    /**
     * Disposes every cached chart, its resize observer and any pending frame. The canvas rebuilds
     * its DOM on a full render, so without this a cached instance stays bound to a removed element
     * and its tooltip renders into a detached container (ECharts throws "Cannot set properties of
     * null (setting 'innerHTML')" from TooltipHTMLContent).
     */
    export function disposeAll(): void {
        Object.keys(chartInstances).forEach(id => {
            chartInstances[id]?.dispose();
            delete chartInstances[id];
        });

        Object.keys(chartObservers).forEach(id => {
            chartObservers[id]?.disconnect();
            delete chartObservers[id];
        });

        Object.keys(renderFrames).forEach(id => {
            cancelAnimationFrame(renderFrames[id]);
            delete renderFrames[id];
        });
    }

    export function render(element: JQuery, component: IComponent) {
        const chartEl = element.find('.chart').get(0) as HTMLDivElement;
        if (!chartEl)
            return;

        if (renderFrames[component.id])
            cancelAnimationFrame(renderFrames[component.id]);

        const performUpdate = () => {
            let chart: echarts.ECharts | undefined = chartInstances[component.id];

            // The canvas can rebuild its DOM without a full dispose (e.g. a group re-render),
            // leaving the cached instance bound to a removed element. Re-init when the element
            // changed so rendering and tooltips never target a detached container.
            if (chart && chart.getDom() !== chartEl) {
                chart.dispose();
                chartObservers[component.id]?.disconnect();
                delete chartObservers[component.id];
                delete chartInstances[component.id];
                chart = undefined;
            }

            if (!chart) {
                const created = echarts.init(chartEl);
                chart = created;
                chartInstances[component.id] = created;

                // Get the DOM elements to observe
                const rootContainer = element.get(0);
                const cardBody = element.find('.card-body').get(0);

                // Setup the single ResizeObserver instance to observe both elements below
                if (rootContainer) {
                    const observer = new ResizeObserver(() => {
                        created.resize();
                    });

                    // Observe the main component element (.component.widget.grid-stack-item)
                    observer.observe(rootContainer);

                    // Observe the inner body element (e.g., if title/hint are added/removed)
                    if (cardBody) {
                        observer.observe(cardBody);
                    }

                    // Store the single observer
                    chartObservers[component.id] = observer;
                }
            }

            const chartData = resolveAndTransformData(component);
            const options = buildOptions(component as IDashboardComponent, chartData);

            // If a tooltip is showing while the chart re-renders, ECharts re-shows it from a
            // deferred callback (_keepShow) using the last hover coordinates. That callback can
            // fire after the tooltip's DOM is torn down and throw "Cannot set properties of null
            // (setting 'innerHTML')" from TooltipHTMLContent. hideTip clears the stored
            // coordinates, so the deferred re-show is never scheduled.
            chart.dispatchAction({ type: 'hideTip' });

            chart.setOption(options, true);

            delete renderFrames[component.id];
        }
        renderFrames[component.id] = requestAnimationFrame(performUpdate);
    }

    function buildOptions(component: IDashboardComponent, data: ChartDataModel): any {
        const options: any = {
            tooltip: { trigger: 'item', confine: true, appendTo: 'body' },
            series: []
        };

        const colors = resolveColors(component);
        const ctx: ChartContext = { component, data, options, colors };

        composeSharedTitle(ctx);
        composeSharedTooltip(ctx);
        composeSharedLegend(ctx);

        if (component.type === ComponentType.pieChart) {
            composePieSeries(ctx);
        }
        else {
            composeCartesianLayout(ctx);
            composeCartesianAxes(ctx);
            composeCartesianSeries(ctx);
        }

        return options;
    }

    function composeSharedTitle(ctx: ChartContext) {
        const { data, options } = ctx;
        if (data.errorMessage) {
            options.title = {
                text: `Data Error: ${data.errorMessage}`,
                left: 'center', top: 'center',
                textStyle: { color: '#D32F2F', fontSize: 12 }
            };
        } else if (!data.hasRealData) {
            options.title = {
                text: 'Example Data', subtext: 'Configure chart to view actual data',
                left: 'center', top: 'center',
                textStyle: { color: '#999' }
            };
        } else if (data.series.length === 0) {
            options.title = {
                text: 'No Data Found',
                left: 'center', top: 'center',
                textStyle: { color: '#666' }
            };
        }
    }

    function resolveFormatter(component: IDashboardComponent, type: 'dataLabel' | 'legend' | 'tooltip' | 'yAxis' | 'xAxis') {
        const styling = component.styling || {};

        let config: IValueFormatConfig | undefined;
        if (type === 'dataLabel')
            config = styling.dataLabelValueFormatting;
        else if (type === 'legend')
            config = styling.legendValueFormatting;
        else if (type === 'tooltip')
            config = styling.tooltipValueFormatting;
        else if (type === 'yAxis')
            config = styling.yAxisValueFormatting;
        else if (type === 'xAxis')
            config = styling.xAxisValueFormatting;

        const defaultFormat = (type === 'legend' || type === 'xAxis') ? 'Category' : (type === 'tooltip' ? 'Category: Value' : 'Value');
        const format = config?.format || defaultFormat;
        const factor = config?.factor ?? 1;
        const decimals = config?.decimals ?? 0;
        const prefix = config?.prefix || '';
        const suffix = config?.suffix || '';

        const formatValue = (val: any): string => {
            if (val === null || val === undefined) return '-';

            let num = Number(val);

            if (isNaN(num))
                return `${prefix}${val}${suffix}`;

            num = num * factor;
            const localeStr = num.toLocaleString(undefined, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });

            return `${prefix}${localeStr}${suffix}`;
        };

        return (params: any) => {
            let rawVal: any;
            if (params && typeof params === 'object' && 'value' in params) {
                rawVal = params.value;
                if (Array.isArray(rawVal) && component.type !== ComponentType.pieChart) {
                    const yIndex = params.encode?.y?.[0] ?? 1;
                    rawVal = rawVal[yIndex];
                }
            } else
                rawVal = params;

            const valStr = formatValue(rawVal);

            // Axes usually just want the formatted string (Value or Category)
            if (type === 'yAxis' || type === 'xAxis')
                return valStr;

            // For others, handle rich formats
            const pctStr = (params && params.percent) ? `${params.percent}%` : '';
            const nameStr = (params && params.name) ? params.name : '';

            switch (format) {
                case 'Category': return nameStr;
                case 'Value': return valStr;
                case 'Percent': return pctStr;
                case 'Category: Value': return `${nameStr}: ${valStr}`;
                case 'Category: Percent': return `${nameStr}: ${pctStr}`;
                case 'Category: Value (Percent)': return `${nameStr}: ${valStr} (${pctStr})`;
                case 'Value (Percent)': return `${valStr} (${pctStr})`;
                default: return valStr;
            }
        };
    }

    function composeSharedTooltip(ctx: ChartContext) {
        const { options, component } = ctx;
        const styling = component.styling || {};
        const triggerPref = styling.tooltipTrigger;

        let trigger = 'item';
        if (triggerPref === 'None')
            trigger = 'none';
        else if (triggerPref === 'Axis')
            trigger = 'axis';
        else if (component.type === ComponentType.pieChart)
            trigger = 'item';

        const bgColor = hexToRgba(styling.tooltipBgColor || '#323232', styling.tooltipBgOpacity ?? 95);
        const textStyle = mapFontStyleToECharts(styling.tooltipValueFormatting?.textStyle, '#ffffff');
        const formatter = resolveFormatter(component, 'tooltip');

        options.tooltip = {
            show: trigger !== 'none',
            trigger: trigger,
            backgroundColor: bgColor,
            textStyle: textStyle,
            borderWidth: 0,
            padding: 10,
            confine: true,
            appendTo: 'body',
            formatter: (params: any) => {
                // Axis Trigger sends an array of items
                if (Array.isArray(params)) {
                    let html = `<div style="font-weight:bold;margin-bottom:5px">${params[0].name}</div>`;
                    params.forEach(p => {
                        html += `<div>${p.marker} ${formatter(p)}</div>`;
                    });
                    return html;
                } else
                    return `${params.marker} ${formatter(params)}`;
            }
        };
    }

    function composeSharedLegend(ctx: ChartContext) {
        const { component, options } = ctx;
        if (component.layout?.showLegend === false) {
            options.legend = { show: false };
            return;
        }

        const style = component.layout?.legendStyle || {};
        const styling = component.styling || {};

        let left = 'center', top = 'auto', bottom = 'auto', right = 'auto';
        let orient = 'horizontal';

        if (style.verticalPosition === 'middle')
            orient = 'vertical';

        if (style.horizontalPosition === 'left')
            left = '0%';
        else if (style.horizontalPosition === 'right') {
            left = 'auto';
            right = '0%';
        }

        if (style.verticalPosition === 'top')
            top = '0%';
        else if (style.verticalPosition === 'middle')
            top = 'middle';
        else
            bottom = '0%';

        const outlineColor = styling.outlineColor || '#ffffff';
        const textStyle = mapFontStyleToECharts(styling.legendValueFormatting?.textStyle, '#666666');

        const legendValueMap: Record<string, number> = {};

        if (component.type === ComponentType.pieChart && options.series?.[0]?.data) {
            options.series[0].data.forEach((d: any) => {
                if (d.name)
                    legendValueMap[d.name] = d.value;
            });
        } else if (ctx.data.series) {
            ctx.data.series.forEach(s => {
                if (s.name) {
                    legendValueMap[s.name] = s.data.reduce((acc: number, val) => acc + (val || 0), 0);
                }
            });
        }

        const baseFormatter = resolveFormatter(component, 'legend');

        const legendFormatter = (name: string) => {
            const val = legendValueMap[name];
            if (val !== undefined) {
                return baseFormatter({ name: name, value: val });
            }
            return baseFormatter({ name: name });
        };

        options.legend = {
            show: true,
            type: 'scroll',
            orient: orient,
            left, right, top, bottom,
            textStyle: textStyle,
            itemStyle: {
                borderColor: outlineColor,
                borderWidth: 2
            },
            formatter: legendFormatter
        };
    }

    function composeCartesianLayout(ctx: ChartContext) {
        const { component, options } = ctx;
        const layout = component.layout || {};

        let top = layout.chartMarginTop ?? 15;
        let bottom = layout.chartMarginBottom ?? 10;
        let left = layout.chartMarginLeft ?? 10;
        let right = layout.chartMarginRight ?? 10;

        options.grid = {
            top: top + '%',
            bottom: bottom + '%',
            left: left + '%',
            right: right + '%',
            containLabel: true
        };
    }

    function composeCartesianAxes(ctx: ChartContext) {
        const { component, data, options } = ctx;
        const styling = component.styling || {};
        const layout = component.layout || {};
        const isBar = component.type === ComponentType.barChart;

        const xAxisFormatter = resolveFormatter(component, 'xAxis');
        const xAxisStyle = mapFontStyleToECharts(styling.xAxisValueFormatting?.textStyle, '#666666');

        options.xAxis = {
            type: 'category',
            data: data.labels,
            boundaryGap: isBar,
            axisLabel: {
                rotate: styling.xAxisLabelRotation || 0,
                color: xAxisStyle.color,
                fontSize: xAxisStyle.fontSize,
                fontWeight: xAxisStyle.fontWeight,
                fontStyle: xAxisStyle.fontStyle,
                interval: 'auto',
                formatter: xAxisFormatter
            },
            axisLine: { lineStyle: { color: '#ccc' } },
            axisTick: { show: false }
        };

        const isLog = styling.yAxisScale === 'Logarithmic';
        const yAxisFormatter = resolveFormatter(component, 'yAxis');
        const yAxisStyle = mapFontStyleToECharts(styling.yAxisValueFormatting?.textStyle, '#666666');

        const commonY = {
            type: isLog ? 'log' : 'value',
            min: layout.yAxisMin ?? undefined,
            max: layout.yAxisMax ?? undefined,
            nameTextStyle: { color: '#999', padding: [0, 0, 0, 10] },
            axisLabel: {
                color: yAxisStyle.color,
                fontSize: yAxisStyle.fontSize,
                fontWeight: yAxisStyle.fontWeight,
                fontStyle: yAxisStyle.fontStyle,
                formatter: yAxisFormatter
            },
            splitLine: {
                show: styling.yAxisGridLines !== 'None',
                lineStyle: { type: 'dashed', color: '#eee' }
            },
            axisLine: { show: false },
            axisTick: { show: false }
        };

        options.yAxis = [
            { ...commonY, name: styling.yAxisLeftTitle },
        ];
    }

    function composeCartesianSeries(ctx: ChartContext) {
        const { component, data, options, colors } = ctx;
        const styling = component.styling || {};
        const layout = component.layout || {};

        const isStacked = layout.stackingMode === 'Normal';
        const isArea = layout.chartStyle === 'Area';
        const labelFormatter = resolveFormatter(component, 'dataLabel');
        const labelTextStyle = mapFontStyleToECharts(styling.dataLabelValueFormatting?.textStyle, '#333333');
        const markerStyle = layout.markerStyle || 'Cycle';
        const showSymbol = markerStyle !== 'None';
        const forceSymbol = markerStyle === 'Circle' ? 'circle' : undefined; // undefined lets ECharts cycle defaults

        const markLineData: any[] = [];
        if (layout.referenceLines && layout.referenceLines.length > 0) {
            layout.referenceLines.forEach(ref => {
                const item: any = {
                    label: { formatter: ref.label || '', position: 'end', color: ref.color || '#333' },
                    lineStyle: { color: ref.color || '#333', type: ref.lineStyle || 'dashed', width: 2 }
                };

                if (ref.type === 'Constant')
                    item.yAxis = ref.value;
                else {
                    const calcVal = calculateReferenceValue(ref.type as any, data.series, isStacked);
                    if (calcVal !== undefined)
                        item.yAxis = calcVal;
                }

                if (item.yAxis !== undefined)
                    markLineData.push(item);
            });
        }

        const LINE_STYLES = ['solid', 'dashed', 'dotted'];
        const SYMBOLS = ['circle', 'rect', 'triangle', 'diamond', 'pin', 'arrow'];

        data.series.forEach((s, index) => {
            const color = colors[index % colors.length];
            const lineStyleIndex = index % LINE_STYLES.length;
            const symbolIndex = Math.floor(index / LINE_STYLES.length) % SYMBOLS.length;
            const seriesItem: any = {
                name: s.name,
                type: component.type === ComponentType.lineChart ? 'line' : 'bar',
                data: s.data,
                yAxisIndex: 0,
                itemStyle: { color: color },
                label: {
                    show: layout.showDataLabels,
                    position: 'top',
                    formatter: labelFormatter,
                    color: labelTextStyle.color,
                    fontSize: labelTextStyle.fontSize,
                    fontWeight: labelTextStyle.fontWeight,
                    fontStyle: labelTextStyle.fontStyle
                }
            };

            if (seriesItem.type === 'line') {
                seriesItem.smooth = layout.lineSmooth ?? false;
                seriesItem.connectNulls = layout.connectNulls ?? true;
                seriesItem.showSymbol = showSymbol;
                seriesItem.symbol = forceSymbol || SYMBOLS[symbolIndex];
                seriesItem.symbolSize = 8;
                seriesItem.lineStyle = {
                    type: LINE_STYLES[lineStyleIndex],
                    width: layout.lineWidth ?? 2
                };

                if (isStacked)
                    seriesItem.stack = 'total';

                if (isArea) {
                    seriesItem.areaStyle = {
                        opacity: 0.3,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: color },
                            { offset: 1, color: 'rgba(255, 255, 255, 0)' }
                        ])
                    };
                }
            } else {
                // Bar Chart logic
                if (isStacked)
                    seriesItem.stack = 'total';
                seriesItem.barMaxWidth = 20;
            }

            // Attach Reference Lines to the *first* series so they appear on the chart regardless of other series visibility
            if (index === 0 && markLineData.length > 0) {
                seriesItem.markLine = {
                    data: markLineData,
                    symbol: ['none', 'none'], // Remove arrowheads from reference lines
                    silent: true, // Reference lines should not be interactive
                    animation: false
                };
            }

            options.series.push(seriesItem);
        });
    }

    function composePieSeries(ctx: ChartContext) {
        const { component, data, options, colors } = ctx;
        const layout = component.layout || {};
        const styling = component.styling || {};

        if (data.series.length === 0)
            return;

        const primarySeries = data.series[0];
        const pieData = primarySeries.data.map((val, idx) => ({
            name: data.labels[idx],
            value: val
        }));

        const isDoughnut = layout.chartStyle === 'Doughnut';
        const useCustomGeom = layout.enableCustomGeometry === true;

        const defaultOuter = 75;
        const defaultInner = isDoughnut ? 45 : 0;
        const defaultCx = 50;
        const defaultCy = 50;

        const finalOuter = useCustomGeom ? (layout.pieOuterRadius ?? defaultOuter) : defaultOuter;
        const finalInner = (isDoughnut && useCustomGeom) ? (layout.pieInnerRadius ?? 45) : defaultInner;
        const finalCx = useCustomGeom ? (layout.pieCenterX ?? defaultCx) : defaultCx;
        const finalCy = useCustomGeom ? (layout.pieCenterY ?? defaultCy) : defaultCy;

        const outlineColor = styling.outlineColor || '#ffffff';
        const labelFormatter = resolveFormatter(component, 'dataLabel');
        const labelTextStyle = mapFontStyleToECharts(styling.dataLabelValueFormatting?.textStyle, '#333333');

        options.series.push({
            name: primarySeries.name,
            type: 'pie',
            radius: [`${finalInner}%`, `${finalOuter}%`],
            center: [`${finalCx}%`, `${finalCy}%`],
            roseType: (layout.roseType && layout.roseType !== 'None') ? layout.roseType.toLowerCase() : undefined,
            avoidLabelOverlap: true,
            itemStyle: {
                borderRadius: 5,
                borderColor: outlineColor,
                borderWidth: 2
            },
            label: {
                show: layout.showDataLabels,
                formatter: labelFormatter,
                distanceToLabelLine: 5,
                color: labelTextStyle.color,
                fontSize: labelTextStyle.fontSize,
                fontWeight: labelTextStyle.fontWeight,
                fontStyle: labelTextStyle.fontStyle
            },
            labelLine: {
                show: layout.showDataLabels,
                length: 10,
                length2: 10,
                smooth: 0.2
            },
            labelLayout: {
                hideOverlap: true
            },
            data: pieData
        });

        options.color = colors;
    }

    function resolveAndTransformData(component: IComponent): ChartDataModel {
        let rawData: IDataQueryResult | null = null;
        let dataQuery: IDataQuery | undefined;

        const definition = componentManager.getDefinition(component.type);
        if (definition) {
            const props = componentManager.getDataQueryPropertyNames(definition);
            for (const name of props) {
                const d = (component as any)[name] as IDataQuery;
                const cache = dataManager.getCachedResult(`${component.id}.${name}`);
                if (cache) {
                    rawData = cache;
                    dataQuery = d;
                    break;
                }
            }
        }

        if (!rawData)
            return getMockData(component);

        return transformRealData(rawData, dataQuery!, component);
    }

    function transformRealData(result: IDataQueryResult, dataQuery: IDataQuery, component: IComponent): ChartDataModel {
        const rows = result.ObjectResult || [];
        const agg = dataQuery.aggregation;
        if (!agg)
            return { labels: [], series: [], hasRealData: true };

        const groupByCol = agg.groupBy;
        const groupByHeader = getHeaderName(groupByCol!, component);
        const breakDownCol = agg.breakDownBy;
        const breakDownHeader = breakDownCol ? getHeaderName(breakDownCol, component) : null;
        const metrics = agg.metrics || [];
        const labelSet = new Set<string>();
        const rowMap = new Map<string, any>();

        rows.forEach(r => {
            if (r[groupByHeader] != null) {
                const label = String(r[groupByHeader]);
                labelSet.add(label);

                if (breakDownHeader && r[breakDownHeader] != null) {
                    const bdVal = String(r[breakDownHeader]);
                    rowMap.set(`${label}|${bdVal}`, r);
                } else
                    rowMap.set(label, r);
            }
        });

        let orderedLabels = Array.from(labelSet);

        const displaySortOrders = (component as IDashboardComponent).layout?.displaySortOrders;
        if (displaySortOrders && displaySortOrders.length > 0)
            orderedLabels = sortLabels(orderedLabels, displaySortOrders, rows, groupByHeader, component, dataQuery);
        else
            orderedLabels.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

        const finalLabels = orderedLabels.map(l => formatDateByGrain(l, groupByCol?.timeGrain));
        const seriesList: any[] = [];

        if (breakDownHeader) {
            const distinctBreakdowns = Array.from(new Set(rows.map(r => String(r[breakDownHeader])))).sort();
            distinctBreakdowns.forEach(bdVal => {
                metrics.forEach((m, mIdx) => {
                    const header = getExpectedMetricHeader(component, dataQuery, m, mIdx);
                    const dataPoints = orderedLabels.map(lbl => {
                        const row = rowMap.get(`${lbl}|${bdVal}`);
                        return row && row[header] !== undefined ? Number(row[header]) : null;
                    });

                    seriesList.push({
                        name: `${bdVal} - ${m.alias || header}`,
                        data: dataPoints,
                        metricConfig: m,
                        breakdownValue: bdVal
                    });
                });
            });
        } else {
            metrics.forEach((m, mIdx) => {
                const header = getExpectedMetricHeader(component, dataQuery, m, mIdx);
                const dataPoints = orderedLabels.map(lbl => {
                    const row = rowMap.get(lbl);
                    return row && row[header] !== undefined ? Number(row[header]) : null;
                });

                seriesList.push({
                    name: m.alias || header,
                    data: dataPoints,
                    metricConfig: m
                });
            });
        }

        return {
            labels: finalLabels,
            series: seriesList,
            hasRealData: true,
            errorMessage: result.Error || undefined
        };
    }

    function sortLabels(labels: string[], sortOrders: IDataQuerySortOrder[], rows: any[], groupByHeader: string, component: IComponent, dataQuery: IDataQuery): string[] {
        const metricSorts = sortOrders.filter(s => appn.utils.hasValue(s.metricIndex));
        const labelMetricMaps = new Map<number, Map<string, number>>();

        metricSorts.forEach(s => {
            if (labelMetricMaps.has(s.metricIndex!))
                return;

            const metric = dataQuery.aggregation?.metrics?.[s.metricIndex!];
            if (!metric)
                return;

            const metricHeader = getExpectedMetricHeader(component, dataQuery, metric, s.metricIndex!);
            const map = new Map<string, number>();

            rows.forEach(r => {
                const key = String(r[groupByHeader]);
                const val = Number(r[metricHeader]);
                const current = map.get(key) || 0;
                map.set(key, current + (isNaN(val) ? 0 : val));
            });
            labelMetricMaps.set(s.metricIndex!, map);
        });

        const sorted = [...labels];
        sorted.sort((a, b) => {
            for (const order of sortOrders) {
                let comparison = 0;
                const isDesc = order.type === 'Desc';

                if (order.column && order.column.columnId === dataQuery.aggregation?.groupBy?.columnId)
                    comparison = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
                else if (appn.utils.hasValue(order.metricIndex)) {
                    const map = labelMetricMaps.get(order.metricIndex!);
                    const valA = map?.get(a) || 0;
                    const valB = map?.get(b) || 0;
                    comparison = valA - valB;
                }

                if (comparison !== 0)
                    return isDesc ? -comparison : comparison;
            }
            return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });

        return sorted;
    }

    function resolveColors(component: IDashboardComponent): string[] {
        if (component.styling?.colorPalette === 'Custom' && component.styling?.customColorPalette?.length)
            return component.styling.customColorPalette;

        const paletteName = component.styling?.colorPalette || 'Vibrant';
        return DashColorPalettes[paletteName] || DashColorPalettes['Vibrant'];
    }

    function getMockData(component: IComponent): ChartDataModel {
        if (component.type === ComponentType.pieChart) {
            return {
                labels: ['Electronics', 'Books', 'Apparel', 'Home'],
                series: [{ name: 'Sales', data: [335, 310, 234, 135], metricConfig: {} as any }],
                hasRealData: false
            };
        }
        return {
            labels: ['Jan', 'Feb', 'Mar', 'Apr'],
            series: [{ name: 'Users', data: [820, 932, 901, 934], metricConfig: {} as any }],
            hasRealData: false
        };
    }

    function mapFontStyleToECharts(style: IFontStyle | undefined, defaultColor: string = '#333'): any {
        if (!style)
            return { color: defaultColor };

        const sizeMap: { [key: string]: number } = {
            'Small': 12,
            'Medium': 14,
            'Large': 16
        };

        return {
            color: style.color || defaultColor,
            fontSize: sizeMap[style.size || 'Medium'] || 12,
            fontWeight: style.decor === 'Bold' ? 'bold' : 'normal',
            fontStyle: style.decor === 'Italic' ? 'italic' : 'normal'
        };
    }

    function calculateReferenceValue(type: 'Average' | 'Min' | 'Max', series: any[], isStacked: boolean): number | undefined {
        if (!series || series.length === 0)
            return undefined;

        const values: number[] = [];
        if (isStacked) {
            const pointCount = series[0].data.length;
            for (let i = 0; i < pointCount; i++) {
                let sum = 0;
                let hasData = false;
                series.forEach(s => {
                    const v = s.data[i];
                    if (v != null && typeof v === 'number' && !isNaN(v)) {
                        sum += v;
                        hasData = true;
                    }
                });
                if (hasData)
                    values.push(sum);
            }
        } else {
            series.forEach(s => {
                s.data.forEach((v: any) => {
                    if (v != null && typeof v === 'number' && !isNaN(v))
                        values.push(v);
                });
            });
        }

        if (values.length === 0)
            return undefined;

        switch (type) {
            case 'Average': return values.reduce((a, b) => a + b, 0) / values.length;
            case 'Min': return values.reduce((a, b) => a < b ? a : b);
            case 'Max': return values.reduce((a, b) => a > b ? a : b);
            default: return undefined;
        }
    }
}
